import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { StudioSwatchChips } from './StudioSwatchChips';
import PhotoFilterIcon from '@mui/icons-material/PhotoFilter';

import { OperationService } from '../../../services';
import type { OperationResult, SourceImageRef } from '../../../domain';
import {
  DEFAULT_IMAGE_TO_IMAGE_STATE,
  buildImageToImageRequest,
  hasSourceImage,
  type ImageToImageFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { mergePromptWithSwatches, type StudioParams } from '../../shared/panels/swatchSelection';
import { ProviderModelPicker } from './ProviderModelPicker';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  swatchPrompt?: string;
  studioParams?: StudioParams;
  onResult?: (result: OperationResult) => void;
}

export const ImageToImagePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  source,
  swatchPrompt,
  studioParams,
  onResult,
}) => {
  const [state, setState] = useState<ImageToImageFormState>(DEFAULT_IMAGE_TO_IMAGE_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.imageToImage);

  const hasSource = hasSourceImage(source);
  const promptHasFragments = (studioParams?.promptFragments?.length ?? 0) > 0;
  const promptOk = state.prompt.trim().length > 0 || promptHasFragments;

  const handleSubmit = async () => {
    if (!source) {
      setValidationError('A source image is required to transform.');
      return;
    }
    try {
      const sid = await ensureSession();
      const promptWithBrackets = mergePromptWithSwatches(state.prompt, swatchPrompt ?? '');
      const request = buildImageToImageRequest(
        { ...state, prompt: promptWithBrackets },
        sid,
        source,
        studioParams,
      );
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  const submitDisabled =
    mutation.isPending || isCreatingSession || !hasSource || !promptOk;

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <PhotoFilterIcon fontSize="small" />
        <Typography variant="subtitle2">Transform</Typography>
      </Stack>

      {!hasSource && (
        <Alert severity="info">Add or select a source image to transform.</Alert>
      )}

      <ProviderModelPicker
        model={state.model}
        onModelChange={m => setState(s => ({ ...s, model: m }))}
        disabled={mutation.isPending || isCreatingSession}
        label="Image model"
        opKind="ImageToImage"
      />

      <TextField
        label="Prompt"
        value={state.prompt}
        onChange={e => setState(s => ({ ...s, prompt: e.target.value }))}
        placeholder="Describe the transformation"
        multiline
        minRows={2}
        maxRows={5}
        size="small"
        fullWidth
        required={!promptHasFragments}
      />

      <StudioSwatchChips studioParams={studioParams} />

      <Box>
        <Typography variant="caption" color="text.secondary">
          Strength · {state.strength.toFixed(2)}
        </Typography>
        <Slider
          value={state.strength}
          onChange={(_, v) => setState(s => ({ ...s, strength: Array.isArray(v) ? v[0] : v }))}
          min={0}
          max={1}
          step={0.05}
          size="small"
        />
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={state.preserveComposition}
            onChange={e => setState(s => ({ ...s, preserveComposition: e.target.checked }))}
            size="small"
          />
        }
        label="Preserve composition"
      />

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Transform failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <PhotoFilterIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Transforming…' : 'Transform'}
        </Button>
      </Box>
    </Stack>
  );
};
